/**
 * motionPrefs — the single accessibility seam every canvas VFX / atmosphere /
 * camera-shake system reads to respect reduced-motion + photosensitivity.
 *
 * Before this, reduced-motion lived ONLY in two CSS files (DOM transitions) —
 * nothing reached the Pixi canvas, so the growing atmosphere/VFX layers had no
 * way to honour the project's "respect reduced-motion / photosensitivity"
 * constraint. This resolves the UserPrefs `accessibility` slice into concrete
 * booleans/scalars, defers `reduceMotion: 'auto'` to the OS
 * `prefers-reduced-motion` query, and caches the slice (kept fresh via
 * subscribeUserPrefs) so a per-frame getMotionPrefs() call costs nothing.
 *
 * Non-React on purpose — Pixi systems (AtmosphereLayer, CameraEffects,
 * VFXSystem, ScreenEffectManager) read it directly. React consumers use the
 * useReducedMotion / useMotionPrefs hooks, which wrap this.
 *
 * Mirrors the cached-slice + subscribe pattern in systems/combat/fctLabel.ts.
 */
import { storage } from '../../services/StorageService';
import type { AccessibilityPrefs } from '../../../shared/userprefs/schema';

export interface ResolvedMotionPrefs {
  /** Reduce/stop non-essential motion (LFO breathing, parallax sway, shake). */
  reduceMotion: boolean;
  /** Suppress full-screen combat flashes (photosensitivity). */
  disableScreenFlash: boolean;
  /** 0..1 camera-shake scalar (0 = no shake). Forced to 0 when reduceMotion. */
  shakeIntensity: number;
  /** Use the colorblind-safe combat palette instead of red/green. */
  colorblindSafe: boolean;
}

const FALLBACK: AccessibilityPrefs = {
  schemaVersion: 1,
  reduceMotion: 'auto',
  disableScreenFlash: false,
  shakeIntensity: 1,
  colorblindSafe: false,
};

let cached: AccessibilityPrefs | null = null;

function slice(): AccessibilityPrefs {
  if (cached === null) {
    try {
      cached = storage.loadUserPrefs().accessibility;
      storage.subscribeUserPrefs((p) => { cached = p.accessibility; });
    } catch {
      cached = FALLBACK; // storage unavailable (tests / SSR) — safe defaults
    }
  }
  return cached;
}

/** Cached `prefers-reduced-motion` MediaQueryList + current match (W2-2,
 *  2026-09-29). The old probe built a fresh MediaQueryList on every call and
 *  per-frame callers (CameraEffects, atmosphere drivers, MobRenderer, …) made
 *  that the #3 production CPU offender. A `change` subscription keeps the
 *  cached boolean live, so a system setting flipped mid-session still takes
 *  effect without a reload — same semantics as the fresh read, one probe. */
interface ReducedMotionQuery {
  mql: MediaQueryList;
  matches: boolean;
  detach: () => void;
}

let reducedMotionQuery: ReducedMotionQuery | null = null;

/** Whether the OS asks for reduced motion (cached MediaQueryList read). */
function osPrefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  if (reducedMotionQuery !== null) return reducedMotionQuery.matches;
  try {
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (): void => {
      if (reducedMotionQuery !== null && reducedMotionQuery.mql === mql) {
        reducedMotionQuery.matches = mql.matches;
      }
    };
    let detach: () => void;
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      detach = () => mql.removeEventListener('change', onChange);
    } else if (typeof mql.addListener === 'function') {
      // Safari < 14 fallback — same live-update contract.
      mql.addListener(onChange);
      detach = () => mql.removeListener(onChange);
    } else {
      detach = () => { /* no listener API — matches are read once at probe */ };
    }
    reducedMotionQuery = { mql, matches: mql.matches, detach };
    return reducedMotionQuery.matches;
  } catch {
    // storage/window unavailable — safe default; keep uncached so a later
    // call can still succeed (same retry semantics as the old probe).
    return false;
  }
}

/** Resolve the accessibility slice into concrete motion knobs. */
export function getMotionPrefs(): ResolvedMotionPrefs {
  const p = slice();
  const reduce = p.reduceMotion === 'on' || (p.reduceMotion === 'auto' && osPrefersReducedMotion());
  return {
    reduceMotion: reduce,
    disableScreenFlash: p.disableScreenFlash,
    shakeIntensity: reduce ? 0 : p.shakeIntensity,
    colorblindSafe: p.colorblindSafe,
  };
}

/** Convenience: should non-essential motion be reduced/stopped right now? */
export function prefersReducedMotion(): boolean {
  return getMotionPrefs().reduceMotion;
}

/** Test seam — drop the cached slice AND the cached media query (detaching its
 *  `change` listener) so specs can re-prime both. */
export function __resetMotionPrefsForTests(): void {
  cached = null;
  if (reducedMotionQuery !== null) {
    try { reducedMotionQuery.detach(); } catch { /* listener already gone */ }
    reducedMotionQuery = null;
  }
}
